import Page from '@/gf/components/Page'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import { US_CENTER } from '@/gf/modules/Map'
import { Address, Maybe } from '@/types'
import pick from 'lodash/pick'
import { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import {
  Navigate,
  Route,
  Routes,
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import {
  RequestForApprovalQuery,
  useApproveRequestMutation,
  useAssignUserMutation,
  useRequestForApprovalQuery,
  useRequestOrderLazyQuery,
} from '../_gen/gql'
import Frame from '../components/Frame'
import useGqlClient from '../hooks/useGqlClient'
import useSession from '../hooks/useSession'
import PartDetailsStep from './CreateRequest/PartDetailsStep'
import ProgressBar from './CreateRequest/ProgressBar'
import ReviewStep from './CreateRequest/ReviewStep'
import SelectVendorsStep from './CreateRequest/SelectVendorsStep'
import { CreateRequestState, Priority } from './CreateRequest/types'
import usePersistedState from './CreateRequest/usePersistedState'
import useVendorSelectionType from './CreateRequest/useVendorSelectionType'
import OrderType from './CreateRequest/OrderType'

type InitRequest = RequestForApprovalQuery['searchRequests']['requests'][number]
type IntegrationShippingAddress = Omit<Address, 'id'> & { id: string | null }

const stepsConfig = [
  { label: 'Part Details', stepPath: 'part-details', index: 0 },
  { label: 'Vendors', stepPath: 'vendors/*', index: 1 },
  { label: 'Review', stepPath: 'review', index: 2 },
]

const partsDetailsProgress = (request: CreateRequestState) => {
  const machineProgress = !request.machineInvolved ? 40 : request.machineOrgId ? 40 : 0
  const partsProgress = request.parts?.[0]?.partNumber || request.parts?.[0]?.description ? 50 : 0
  const urgencyProgress = request.urgency?.priority ? 10 : 0
  return machineProgress + partsProgress + urgencyProgress
}

const vendorsProgress = (request: CreateRequestState) => {
  const selectedDealers = request.dealerLocationIds.length > 0 ? 50 : 0
  const location = request.locationId ? 20 : 0
  const dealersProgress = selectedDealers + location

  return request.broadcastToNetwork ? 100 : dealersProgress
}

const stepProgressFns = [
  partsDetailsProgress,
  vendorsProgress,
  (_request: CreateRequestState) => 100,
]

export const evalProprity = (
  insertedAt: DateTime,
  neededBy: Maybe<DateTime>,
  machineDown: boolean
) => {
  if (machineDown) {
    return Priority.HIGH
  }

  if (!neededBy) {
    return Priority.MEDIUM
  }

  if (neededBy.diff(insertedAt).as('days') > 6) {
    return Priority.LOW
  }

  if (neededBy.diff(insertedAt).as('days') > 1) {
    return Priority.MEDIUM
  }

  return Priority.HIGH
}

const ApproveRequestNextForRfq = () => {
  const client = useGqlClient()
  const location = useLocation()
  const navigate = useNavigate()
  const [initRequest, setInitRequest] = useState<InitRequest>()
  const { rfqId } = useParams() as { rfqId: string }
  const [initialized, initializeToggle] = useToggle()
  // Keyed by rfqId (the hook appends the user id): one shared key let picks
  // from one request bleed into another approved right after it.
  const { values, update, reset } = usePersistedState(`aprove-request-next-state-2-${rfqId}`)
  const {
    orgId,
    organization,
    features,
    user: { id: userId },
  } = useSession()
  const [getRequestOrder] = useRequestOrderLazyQuery({ client })
  const [approveRequestMutation, { loading: approveRequestInProgress }] = useApproveRequestMutation(
    { client }
  )
  const [assignUserMutation, { loading: assignUserInProgress }] = useAssignUserMutation({ client })
  const [submitting, setSubmitting] = useState(false)
  const [, msgsMgr] = useMsgs()

  const basePath = `/rfqs/${rfqId}/approve`

  const initForm = (rfq: InitRequest) => {
    const dealerLocationIds = rfq.vendorLinks
      .map((vl) => vl.vendor?.dealerLocation?.id ?? vl.vendor?.id)
      .filter((dlId) => !!dlId)

    const machineOrgId = rfq.orgMachines[0]?.id || null

    update({
      billingCompanyId: rfq.billingCompanyId,
      workOrderNumber: '',
      machineOrgId,
      machineInvolved: !!machineOrgId,
      urgency: {
        machineDown: rfq.machineDown,
        neededByDate: rfq.neededBy
          ? rfq.neededBy
          : rfq.machineDown
            ? rfq.insertedAt.plus({ days: 1 })
            : rfq.insertedAt.plus({ days: 3 }),
        priority: evalProprity(rfq.insertedAt, rfq.neededBy, rfq.machineDown),
      },
      parts: rfq.parts.map((p, i) => ({
        partNumber: p.mpn,
        description: p.description,
        quantity: p.quantity,
        externalId: p.externalId,
        taskNumber: p.taskNumber,
        suggestion: p.suggestion,
        pictures: i === 0 ? rfq.images.map(({ url }) => url) : [],
        jobQuantities:
          p.jobQuantities?.map((jq) => ({ quantity: jq.quantity, jobId: jq.job.id })) ?? [],
        costCodeId: p.costCode?.id ?? null,
        phaseCodeId: p.phaseCode?.id ?? null,
      })),
      comments: rfq.partsRequest ?? '',
      broadcastToNetwork: !rfq.resumeDealer && rfq.vendorLinks.length === 0,
      quickOrder: rfq.resumeDealer ? undefined : false,
      dealerLocationIds,
      vendors: rfq.vendorLinks.map((vl) => ({
        vendorId: vl.vendor.id,
        contactIds: vl.vendorContacts.map((vc) => vc.id),
        deliveryMethod: vl.deliveryMethod,
        accountNumber: vl.accountNumber ?? '',
        fleetioId: null,
        externalVendorId: null,
      })),
      shippingAddress: rfq.shippingAddress as Address,
      locationId: rfq.shippingAddress?.location?.id,
      nearbyReferencePoint: rfq.shippingAddress?.point
        ? pick(rfq.shippingAddress.point, 'lat', 'lng')
        : US_CENTER,
      seeded: true,
    })

    initializeToggle.on()
  }

  const { data } = useRequestForApprovalQuery({
    client,
    variables: { orgId, filter: JSON.stringify(['id_eq', rfqId]) },
    onCompleted({ searchRequests }) {
      const rfq = searchRequests.requests[0]
      setInitRequest(rfq)

      // Seed at most once per request, not once per completion or per mount:
      // onCompleted fires again on refetches, and the component remounts on
      // every navigate-away-and-back or reload — each unguarded seed wiped
      // the buyer's in-progress edits (e.g. their shipping-location pick).
      // `seeded` travels with the per-rfq persisted state; Start Over clears
      // it, so an explicit reset still re-seeds.
      if (!initialized && !values.seeded) {
        initForm(rfq)
      } else {
        initializeToggle.on()
      }
    },
  })

  const org = data?.org ?? null

  const resetForm = () => {
    reset()
    navigate('part-details')
    initForm(initRequest as InitRequest)
  }

  const currentStep = useMemo(() => {
    const matchingStep = stepsConfig.find(
      (step) => !!matchPath({ path: `${basePath}/${step.stepPath}` }, location.pathname)
    )
    const step = matchingStep ?? stepsConfig[0]

    return { ...step, progress: stepProgressFns[step.index](values) }
  }, [location.pathname, values])

  const onPartDetailsSubmitted = () => navigate('order-type')

  const onVendorsSubmitted = () => navigate('review')

  const onApproveRequest = async (poNumber?: string) => {
    if (submitting) return

    setSubmitting(true)
    try {
      const imageUrls = values.parts?.[0].pictures ?? []

      await assignUserMutation({ variables: { userId, rfqId } })

      const locationIds = values.locationId ? [values.locationId as string] : []
      const integrationAddress = values.shippingAddress as IntegrationShippingAddress
      const shippingAddress = integrationAddress ?? { id: null }
      const shippingAddressId = shippingAddress.id

      const result = await approveRequestMutation({
        variables: {
          id: rfqId,
          billingCompanyId: values.billingCompanyId ?? null,
          splitOrder: false,
          orgMachineId: values.machineOrgId || null,
          vendorIds: null,
          vendors: values.vendors.map((v) => ({
            vendorContactIds: v.contactIds,
            deliveryMethod: v.deliveryMethod,
            id: v.vendorId,
            customerNote: null,
            vendorContactId: null,
            pickup: null,
            accountNumber: v.accountNumber,
          })),
          shippingLocationIds: locationIds,
          shippingAddressId: shippingAddressId,
          partsRequest: values.comments ?? null,
          partRequests:
            values.parts?.map((part) => ({
              mpn: part.partNumber,
              description: part.description,
              quantity: part.quantity,
              externalId: part.externalId,
              taskNumber: part.taskNumber,
              suggestion: part.suggestion,
              jobQuantities: part.jobQuantities,
              costCodeId: part.costCodeId,
              phaseCodeId: part.phaseCodeId,
            })) ?? null,
          imageUrls,
          neededBy: values.urgency?.neededByDate.toUTC() ?? null,
          machineDown: values.urgency?.machineDown ?? null,
          quickOrder: !!values.quickOrder,
          broadcastToNetwork: values.broadcastToNetwork,
          purchaseOrder: poNumber ?? null,
        },
      })

      if (result.data) {
        reset()

        if (values.quickOrder) {
          const { data } = await getRequestOrder({
            variables: { orgId: organization.id, filter: `[["id_eq","${rfqId}"]]` },
          })

          const storeOrderId = data?.searchRequests.requests[0]?.storeOrders[0]?.id
          if (storeOrderId) {
            navigate(`/orders/${storeOrderId}/quick-order`)
          } else {
            navigate(`/rfqs/${rfqId}?new=1&init=1`)
          }
        } else {
          navigate(`/rfqs/${rfqId}?new=1&init=1`)
        }
      } else {
        setSubmitting(false)
        showError(result.errors)
      }
    } catch (error) {
      setSubmitting(false)
      showError(error)
    }
  }

  // A rejected approval used to reach only the console, so the approver saw a
  // button that did nothing (GEA-10832). Show the server's reason instead.
  const showError = (error: unknown) => {
    const errors = error as { message?: string }[] | undefined
    const message = Array.isArray(errors)
      ? errors.map((e) => e.message).join(', ')
      : (error as { message?: string } | undefined)?.message

    if (message) {
      msgsMgr.add(message, 'negative')
    } else {
      msgsMgr.addUnknownError()
    }
  }

  const vendorSelectionType = useVendorSelectionType(org)

  if (!org || !initialized) {
    return null
  }

  return (
    <Frame>
      <Page title="Approve Request">
        <div className="flex flex-col gap-y-8 mt-8 pb-12">
          <ProgressBar
            steps={stepsConfig.map((s) => s.label)}
            currentStep={currentStep.index}
            currentStepProgress={currentStep.progress}
          />
          <div className="space-y-4">
            <h3 className="text-2xl text-gray-900">{currentStep.label}</h3>
            <Routes>
              <Route path="" element={<Navigate to="review" replace />} />
              <Route path="details" element={<Navigate to="../part-details" replace />} />
              <Route path="dealers" element={<Navigate to="../order-type" replace />} />
              <Route
                path="part-details"
                element={
                  <PartDetailsStep
                    request={values}
                    updateRequest={update}
                    onSubmit={onPartDetailsSubmitted}
                    billingCompanyRequired={organization.requireBillingCompany ?? false}
                    workOrderNumberRequired={false}
                    strictWorkOrderSelection={organization.strictWorkOrderSelection ?? false}
                    uploadPartsEnabled={false}
                    rfqId={rfqId}
                    submitButtonText="Next"
                    filfillFromInventoryMessageEnabled
                    jobNumbersEnabled={organization.requestsJobNumbersEnabled ?? false}
                    costCodesEnabled={organization.costCodesEnabled ?? false}
                    phaseCodesEnabled={features.phaseCodesEnabled ?? false}
                  />
                }
              />
              <Route
                path="order-type"
                element={
                  <OrderType
                    reset={resetForm}
                    request={values}
                    updateRequest={update}
                    onBackClicked={() => navigate('part-details')}
                    vendorSelectionType={vendorSelectionType}
                  />
                }
              />
              <Route
                path="vendors/*"
                element={
                  <SelectVendorsStep
                    reset={resetForm}
                    request={values}
                    updateRequest={update}
                    onSubmit={onVendorsSubmitted}
                    onBackClicked={() => navigate('order-type')}
                    vendorSelectionType={vendorSelectionType}
                    gearflowNetworkEnabled
                  />
                }
              />
              <Route
                path="review"
                element={
                  <ReviewStep
                    reset={resetForm}
                    request={values}
                    updateRequest={update}
                    onBackClicked={() => navigate('vendors')}
                    onCreateRequestClicked={onApproveRequest}
                    submitButtonText="Approve Request"
                    submitInProgress={
                      approveRequestInProgress || assignUserInProgress || submitting
                    }
                  />
                }
              />
            </Routes>
          </div>
        </div>
      </Page>
    </Frame>
  )
}

// A same-route navigation to another RFQ's approve page would keep one
// instance alive across the rfqId change: useLocalStorage saves on
// [key, value], so the old request's in-memory form would be written under
// the new request's storage key before that request ever loads (and the
// in-memory `initialized` gate would suppress its seed). Keying the instance
// by rfqId remounts instead, giving every request fresh state.
const ApproveRequestNext = () => {
  const { rfqId } = useParams() as { rfqId: string }
  return <ApproveRequestNextForRfq key={rfqId} />
}

export default ApproveRequestNext
